import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useController, useWatch } from 'react-hook-form'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import ChannelSelectionTable from 'subapps/settings/components/ChannelSelectionTable'
import DropdownMenu from 'subapps/settings/components/DropdownMenu'
import {
  EMAIL_TYPE,
  LIVE_CHAT_WIDGET_TYPE,
} from 'subapps/settings/components/ChannelSelectionTable/types'
import {
  CHATS_PREFIX,
  MAILBOXES_PREFIX,
} from 'ducks/integrations/utils/integrationSettingPrefix'
import { selectWidgets } from 'ducks/widgets/selectors/widgets'
import { buildId } from 'util/globalId'
import {
  FORM_KEY_SYNC,
  FORM_KEY_ONLINE_STORE_WIDGET_ID,
  FORM_KEY_INSTALL_ONLINE_STORE_WIDGET_ENABLED,
} from '../constants'

const ALLOWED_TYPES = [EMAIL_TYPE, LIVE_CHAT_WIDGET_TYPE]

export default function ShopifyUpdateStoreConfigure({ store, ...rest }) {
  const { reset, register, getValues, setValue, control } = rest
  const widgets = useSelector(selectWidgets)

  const {
    field: {
      value: onlineStoreWidgetId,
      onChange: onOnlineStoreWidgetIdChange,
    },
  } = useController({
    name: FORM_KEY_ONLINE_STORE_WIDGET_ID,
    control,
  })

  const isInstallOnlineStoreWidgetEnabled = useWatch({
    control,
    name: FORM_KEY_INSTALL_ONLINE_STORE_WIDGET_ENABLED,
  })

  const selectedWidget = useMemo(
    () => {
      return widgets.find(w => w.id === onlineStoreWidgetId)
    },
    [widgets, onlineStoreWidgetId]
  )

  /**
   * mailbox + widget for shopify store settings is in the format {mailboxes.1234: true, mailboxes.999: false}
   * react hook forms is NOT a fan of that as it thinks it's a nested property and can never set the values.
   * after numerous hacks, decided to change the format here to something more usable i.e. channels: {1234: true, 999: false}
   * plays better with TableWithCheckbox and react hook forms
   */
  const saneSettings = useMemo(
    () => {
      const { settings: { settings } = {} } = store || {}

      return Object.entries(settings).reduce((obj, [key, value]) => {
        if (
          !key.startsWith(MAILBOXES_PREFIX) &&
          !key.startsWith(CHATS_PREFIX)
        ) {
          // eslint-disable-next-line no-param-reassign
          obj[key] = value
          return obj
        }

        if (!obj?.channels) {
          // eslint-disable-next-line no-param-reassign
          obj.channels = {}
        }

        if (key.startsWith(MAILBOXES_PREFIX)) {
          const newKey = key.replace(MAILBOXES_PREFIX, '')
          // eslint-disable-next-line no-param-reassign
          obj.channels[buildId('Channel', newKey)] = value
        } else if (key.startsWith(CHATS_PREFIX)) {
          const newKey = key.replace(CHATS_PREFIX, '')
          // eslint-disable-next-line no-param-reassign
          obj.channels[buildId('Widget', newKey)] = value
        }

        return obj
      }, {})
    },
    [store]
  )

  useEffect(
    () => {
      reset({
        ...saneSettings,
        [FORM_KEY_INSTALL_ONLINE_STORE_WIDGET_ENABLED]: !!saneSettings?.[
          FORM_KEY_ONLINE_STORE_WIDGET_ID
        ],
      })
    },
    [saneSettings, reset]
  )

  const onSelectedRowIdsChange = useCallback(
    selectedRowIds => {
      // calculate a diff of previous selected channels state
      // and new state and only setValue on changed selectedIds
      const valuesStateBefore = { ...(getValues()?.channels || {}) }

      Object.entries(valuesStateBefore).forEach(([key, value]) => {
        const selectedValue = selectedRowIds[key] || false
        if (value !== selectedValue) {
          setValue(`channels.${key}`, selectedValue, { shouldDirty: true })
        }
      })
    },
    [setValue, getValues]
  )

  const defaultSelectedRowIds = useMemo(
    () => {
      return saneSettings?.channels || {}
    },
    [saneSettings]
  )

  const onToggleEnableOnlineStoreWidget = useCallback(
    e => {
      if (e?.currentTarget?.checked === false) {
        onOnlineStoreWidgetIdChange(null)
      }
    },
    [onOnlineStoreWidgetIdChange]
  )

  return (
    <div className="grui mt-12">
      <div
        css={[text.styles.fontMedium, text.styles.textBlack]}
        className="grui mb-5"
      >
        Shopify field syncing
      </div>
      <Checkbox
        id="shopifyStoreSettingsSync"
        css={text.styles.textDark}
        {...register(FORM_KEY_SYNC)}
      >
        Update the Groove contact when the first name, last name, or email
        address changes in Shopify
      </Checkbox>
      <div
        css={[
          text.styles.text2Xs,
          text.styles.textMediumDark,
          text.styles.italic,
        ]}
        className="grui ml-12 mt-3"
      >
        Note: This will only update fields going forward
      </div>
      <div className="grui mt-10">
        <div
          css={[text.styles.fontMedium, text.styles.textBlack]}
          className="grui mb-5"
        >
          Groove Widget
        </div>
        <Checkbox
          id="shopifyStoreSettingsInstallWidgetEnabled"
          css={text.styles.textDark}
          disabled={!widgets?.length}
          {...register(FORM_KEY_INSTALL_ONLINE_STORE_WIDGET_ENABLED, {
            onChange: onToggleEnableOnlineStoreWidget,
          })}
        >
          Enable your widget in your Shopify store
        </Checkbox>
        {isInstallOnlineStoreWidgetEnabled && (
          <div className="grui mt-3 ml-12">
            <Dropdown
              overlay={<DropdownMenu data={widgets} />}
              onSelect={onOnlineStoreWidgetIdChange}
              selectedKey={selectedWidget?.id}
              disabled={!widgets?.length}
            >
              <Dropdown.Button size="small">
                {selectedWidget?.name || 'Select a widget'}
              </Dropdown.Button>
            </Dropdown>
          </div>
        )}
      </div>
      <div className="grui mt-10">
        <div css={[text.styles.fontMedium, text.styles.textBlack]}>
          Channels
        </div>
        <p className="grui mt-3 mb-10" css={paragraph.styles.preflight}>
          Choose which channels you want to display this store in.
        </p>
        <ChannelSelectionTable
          onSelectedRowIdsChange={onSelectedRowIdsChange}
          defaultSelectedRowIds={defaultSelectedRowIds}
          displayChannelTypes={ALLOWED_TYPES}
        />
      </div>
    </div>
  )
}
